import React, { Fragment } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, Transition } from '@headlessui/react'
import { Search, Bell, BellOff, LogOut, User, ChevronDown, Menu as MenuIcon, Users as UsersIcon, Settings as SettingsIcon } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { cn } from '../utils/cn'
import BrandMark from './ui/BrandMark'
import { roleLabel } from '../utils/roles'
import { hasAnyPermission } from '../utils/permissions'
import { pageNameForPath } from '../utils/pageTitle'
import useGlobalSearch from '../hooks/useGlobalSearch'
import useRbacDriftCheck from '../hooks/useRbacDriftCheck'
import { NAV_ITEMS } from './sidebarNav.mjs'
import GlobalSearch from './GlobalSearch'

// Only routes that actually exist. Permission-gated entries use the same
// requireAny keys the sidebar does, so the menu can't offer a page that 403s.
// Exported so hooks/useRbacDriftCheck.js can cross-check these requireAny
// strings against the live RBAC catalog alongside the sidebar's.
export const ACCOUNT_LINKS = [
  // Self-service: my own profile/security/preferences (AccountPage.jsx).
  { name: 'My account', href: '/app/account', icon: User },
  { name: 'Public content', href: '/app/settings/landing-content', icon: SettingsIcon, requireAny: ['manage_landing'] },
  // Admin: managing OTHER users' accounts — a distinct action/surface from
  // "My account" above, kept separate on purpose (see AccountPage.jsx's
  // module doc). Lands on the People > Users panel.
  { name: 'Manage users', href: '/app/admin/users', icon: UsersIcon, requireAny: ['manage_users'] },
]

const Header = ({ onMenuClick = () => {} }) => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { isOpen: searchOpen, openSearch, closeSearch } = useGlobalSearch()

  // Shared gate (utils/permissions.js) — was a local duplicate of this exact
  // logic ("Same fallback as Sidebar — it must agree"), which meant the
  // super_admin stale-token fix landing in hasAnyPermission alone wouldn't
  // have reached this menu. Delegating avoids a second copy drifting again.
  const hasAny = (list) => hasAnyPermission(user, list)

  // RBAC Slice 3 — once-per-session diagnostic: for users who can read the
  // full catalog (manage_users), warns if a hardcoded requireAny string
  // (here or in the sidebar) has drifted from the live permission set. See
  // hooks/useRbacDriftCheck.js. Never affects what's rendered.
  useRbacDriftCheck(user, [NAV_ITEMS, ACCOUNT_LINKS])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  // No notifications API exists yet — the bell shows an honest empty state
  // instead of fabricated rows. Re-wire once /api/notifications ships.
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || 'U'

  // Same route→label map that drives document.title (pageTitleLogic.mjs) —
  // this is the bare label, no "IEWRS |" prefix, since the sidebar's own
  // logo already carries the brand. Falls back to "Dashboard" for any
  // path with no entry (shouldn't happen for a real /app/* route, but a
  // heading must never render blank).
  const pageName = pageNameForPath(location.pathname) || 'Dashboard'

  return (
    <header className="bg-white border-b border-gray-100 px-3 sm:px-6 py-3.5 relative z-20">
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        {/* Mobile menu trigger — the sidebar (which carries the full logo
            lockup) is off-canvas below lg. */}
        <button
          onClick={onMenuClick}
          aria-label="Open navigation menu"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 lg:hidden"
        >
          <MenuIcon className="w-5 h-5" strokeWidth={1.75} />
        </button>
        <BrandMark iconClass="h-7" wordmarkClass="text-base" className="hidden sm:inline-flex lg:hidden" />

        {/* Dynamic page title — the current page's real heading, driven by
            the same route map document.title uses (pageTitleLogic.mjs). */}
        <h1 className="min-w-0 max-w-[35vw] shrink truncate text-lg font-bold tracking-tight text-gray-900 sm:max-w-none sm:text-xl">
          {pageName}
        </h1>

        {/* Global search trigger — opens the command-palette GlobalSearch
            modal; also reachable via the app-wide Cmd/Ctrl+K shortcut
            (useGlobalSearch). Not a live-typing input itself: the actual
            query box lives inside the modal so arrow-key/Enter navigation
            has a dedicated, focus-trapped surface. */}
        <div className="flex flex-1 justify-center min-w-0 px-1 sm:px-4">
          <button
            type="button"
            onClick={openSearch}
            aria-haspopup="dialog"
            aria-expanded={searchOpen}
            className="group flex w-full max-w-md items-center gap-2.5 rounded-full border border-gray-200 bg-gray-50/70 px-3.5 py-2.5 text-left text-sm text-gray-400 transition-colors hover:border-gray-300 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
          >
            <Search className="h-4 w-4 shrink-0 text-gray-400" strokeWidth={1.75} />
            <span className="sr-only truncate sm:not-sr-only">Search…</span>
            <kbd className="ml-auto hidden shrink-0 items-center gap-0.5 rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-gray-400 group-hover:border-gray-300 md:inline-flex">
              <span aria-hidden="true">⌘</span>K
            </kbd>
          </button>
        </div>
        <GlobalSearch open={searchOpen} onClose={closeSearch} />

        {/* Right side actions — the desktop rail collapse toggle lives on
            Sidebar; not duplicated here. */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Notifications */}
          <Menu as="div" className="relative">
            <Menu.Button
              className="relative flex items-center justify-center h-11 w-11 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2"
              aria-label="Notifications (no unread)"
            >
              <Bell className="w-5 h-5" strokeWidth={1.75} />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-150"
              enterFrom="opacity-0 scale-95 -translate-y-1"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-80 origin-top-right surface rounded-2xl overflow-hidden focus:outline-none z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                </div>
                {/* Honest empty state — no notifications API exists yet. Not
                    a Menu.Item since there's no action to take; keeps
                    keyboard/arrow-key navigation from landing on a no-op. */}
                <div className="px-4 py-8 flex flex-col items-center text-center" role="presentation">
                  <div className="p-3 bg-gray-100 text-gray-400 rounded-full mb-3">
                    <BellOff className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <p className="text-sm font-medium text-gray-700">No new notifications</p>
                  <p className="text-xs text-gray-500 mt-1">You're all caught up.</p>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>

          <div className="h-6 w-px bg-gray-200 mx-1 hidden sm:block" aria-hidden="true" />

          {/* User menu */}
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center gap-2.5 rounded-full py-1.5 pl-1.5 pr-2.5 text-sm hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2">
              {/* Initials are 12px white text ON the fill, so this chip uses the
                  button ramp (600->700), not `brand-gradient` (500->600): white on
                  the 500 stop is 3.68:1, under AA. The logo chip above may stay on
                  `brand-gradient` because it carries an icon, not text (3:1 bar). */}
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gradient-btn text-xs font-semibold text-white shadow-elev-1">
                {initials}
              </span>
              <span className="hidden md:block text-left leading-tight">
                <span className="block font-medium text-gray-900">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className="block text-xs text-gray-500">
                  {roleLabel(user?.role)}
                </span>
              </span>
              <ChevronDown className="w-4 h-4 text-gray-400 hidden md:block" strokeWidth={1.75} />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-150"
              enterFrom="opacity-0 scale-95 -translate-y-1"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right surface rounded-2xl overflow-hidden focus:outline-none z-50 py-1">
                {/* Identity block — not a Menu.Item, there's nothing to activate. */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 truncate" title={user?.email}>{user?.email}</p>
                  <span className="badge-info mt-2 inline-flex">{roleLabel(user?.role)}</span>
                </div>

                {ACCOUNT_LINKS.filter(link => hasAny(link.requireAny)).map(link => (
                  <Menu.Item key={link.href}>
                    {({ active }) => (
                      <Link
                        to={link.href}
                        className={cn(
                          'flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 transition-colors',
                          active && 'bg-gray-50 text-gray-900'
                        )}
                      >
                        <link.icon className="w-4 h-4 text-gray-400" strokeWidth={1.75} />
                        <span>{link.name}</span>
                      </Link>
                    )}
                  </Menu.Item>
                ))}
                <div className="border-t border-gray-100 my-1" />
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleLogout}
                      className={cn(
                        'flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 transition-colors',
                        active && 'bg-danger-50 text-danger-700'
                      )}
                    >
                      <LogOut className="w-4 h-4 text-gray-400" strokeWidth={1.75} />
                      <span>Sign out</span>
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  )
}

export default Header
